import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { FaCalendar, FaUser, FaBox, FaEuroSign, FaFilter, FaTrash, FaSortNumericDown } from 'react-icons/fa';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminRentals() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || !user.roles?.includes('admin')) {
      navigate('/');
      return;
    }
    fetchRentals();
  }, [user]);

  const fetchRentals = async () => {
    try {
      const response = await axios.get(`${API}/admin/rentals/all`);
      setRentals(response.data);
    } catch (error) {
      console.error('Error fetching rentals:', error);
      toast.error('Erreur lors du chargement des locations');
    } finally {
      setLoading(false);
    }
  };

  const updateRentalStatus = async (rentalId, status) => {
    try {
      await axios.put(`${API}/rentals/${rentalId}`, { status });
      toast.success('Statut mis à jour');
      fetchRentals();
    } catch (error) {
      console.error('Error updating rental:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteRental = async (rentalId) => {
    try {
      await axios.delete(`${API}/rentals/${rentalId}`);
      toast.success('Location supprimée avec succès');
      fetchRentals();
    } catch (error) {
      console.error('Error deleting rental:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const updateRentalPosition = async (rentalId, position) => {
    try {
      await axios.patch(`${API}/rentals/${rentalId}/position?admin_id=${user.id}&position=${position}`);
      toast.success('Position mise à jour');
      fetchRentals();
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Erreur lors de la mise à jour de la position');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-blue-500',
      in_progress: 'bg-purple-500',
      completed: 'bg-[#635BFF]',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const filteredRentals = rentals.filter(rental => {
    if (filter !== 'all' && rental.status !== filter) return false;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        rental.user_name?.toLowerCase().includes(searchLower) ||
        rental.user_email?.toLowerCase().includes(searchLower) ||
        rental.product_name?.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            Gestion des Locations
          </h1>
          <p className="text-gray-600">
            Gérez toutes les demandes de location d'équipements
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <FaFilter className="text-[#0A2540]" />
              <h2 className="text-lg font-semibold text-[#0A2540]">Filtres</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Rechercher (nom, email, produit...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="confirmed">Confirmé</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                onClick={() => {
                  setFilter('all');
                  setSearchTerm('');
                }}
                variant="outline"
              >
                Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Rentals List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-[#0A2540]">
              Locations ({filteredRentals.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredRentals.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Aucune location trouvée
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRentals.map((rental) => (
                  <Card key={rental.id} className="hover:shadow-md transition-all">
                    <CardContent className="p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge className={`${getStatusColor(rental.status)} text-white`}>
                              {rental.status}
                            </Badge>
                            <Badge className={`${rental.payment_status === 'paid' ? 'bg-[#635BFF]' : 'bg-[#00D4FF]'} text-white`}>
                              {rental.payment_status === 'paid' ? 'Payé' : 'Non payé'}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-center gap-2">
                              <FaUser className="text-[#0A2540]" />
                              <span><strong>Client:</strong> {rental.user_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaBox className="text-[#00D4FF]" />
                              <span><strong>Produit:</strong> {rental.product_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaCalendar className="text-blue-500" />
                              <span><strong>Début:</strong> {new Date(rental.start_date).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaCalendar className="text-purple-500" />
                              <span><strong>Fin:</strong> {new Date(rental.end_date).toLocaleDateString('fr-FR')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <FaEuroSign className="text-[#635BFF]" />
                              <span><strong>Total:</strong> {rental.total}€</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-600">
                                <strong>Email:</strong> {rental.user_email}
                              </span>
                            </div>
                          </div>

                          {rental.notes && (
                            <div className="mt-3 p-3 bg-gray-50 rounded">
                              <p className="text-sm text-gray-700">
                                <strong>Notes:</strong> {rental.notes}
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          {/* Position input */}
                          <div className="flex items-center gap-2 p-2 bg-[#635BFF]/5 rounded border border-[#635BFF]/20">
                            <FaSortNumericDown className="text-[#635BFF]" />
                            <span className="text-xs text-gray-600">Position:</span>
                            <Input
                              type="number"
                              min="1"
                              className="w-16 h-8 text-center text-sm"
                              defaultValue={rental.position || 999}
                              onBlur={(e) => {
                                const newPosition = parseInt(e.target.value) || 999;
                                if (newPosition !== (rental.position || 999)) {
                                  updateRentalPosition(rental.id, newPosition);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                            />
                          </div>
                          
                          <Select
                            value={rental.status}
                            onValueChange={(value) => updateRentalStatus(rental.id, value)}
                          >
                            <SelectTrigger className="w-full lg:w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">En attente</SelectItem>
                              <SelectItem value="confirmed">Confirmé</SelectItem>
                              <SelectItem value="in_progress">En cours</SelectItem>
                              <SelectItem value="completed">Terminé</SelectItem>
                              <SelectItem value="cancelled">Annulé</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="outline" 
                                className="w-full lg:w-40 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
                              >
                                <FaTrash className="mr-2" />
                                Supprimer
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Êtes-vous sûr de vouloir supprimer cette location ?
                                  <br /><br />
                                  <strong>Client:</strong> {rental.user_name}<br />
                                  <strong>Produit:</strong> {rental.product_name}<br />
                                  <strong>Total:</strong> {rental.total}€
                                  <br /><br />
                                  Cette action est irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => deleteRental(rental.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Supprimer
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
